import React from "react";
import {IServiceFactory} from "../../service-factory.interface";
import {BookingSearchStrategyType, IBookingSearchStrategyViewModel} from "./booking-search.strategy.interface";
import {makeObservable, observable, runInAction} from "mobx";
import {IBookingSearchResultRecord} from "../../airline-webapi/responses/booking-search.response";
import {BookingSearchResultRecordModel} from "../booking-search-result-record.model";
import {FormModel} from "../../../models/forms/form.model";
import {FormFields, IFormField} from "../../../models/forms/form-field.interface";
import {BookingSearchPaginatorModel, BookingSearchResultPageModel} from "./booking-search-paginator.model";
import {IBookingSearchPagination} from "../../airline-webapi/requests/booking-search.requests";
import {NullableDate, NullableString} from "../../../types/nullable-types";
import {UserSessionStorageKeys} from "../../storage/user-session-storage/user-session-storage-keys";
import {IonIcon} from "@ionic/react";
import { IBookingSearchResultRecordViewModel } from "../booking-search-result-record.view-model.interface";
import {CsvExporter} from "../../../utils/csv-exporter";
import {NullableStation} from "../../stations/station.service.interface";


export abstract class BookingSearchStrategyBase<TFields, TPersistedFields> extends FormModel<TFields> implements IBookingSearchStrategyViewModel {
    constructor(services: IServiceFactory) {
        super(services);

        this._paginator = new BookingSearchPaginatorModel(this.services);

        makeObservable<this, '_paginator' | '_currentPageIndex' | '_isSearchInProgress' | '_selectedRecordKey'>(this, {
            _paginator: observable.ref,
            _currentPageIndex: observable.ref,
            _isSearchInProgress: observable.ref,
            _selectedRecordKey: observable.ref,
        });
    }

    abstract get type(): BookingSearchStrategyType;
    abstract get name(): string;
    abstract get resultsDescription(): string;
    protected abstract _createFilterFields(persistedFields: TPersistedFields | null): FormFields<TFields>;
    abstract renderSearchFields(): React.ReactElement;
    abstract _searchBookings(sessionToken: string, pagination: IBookingSearchPagination): Promise<IBookingSearchResultRecord[]>;
    protected abstract _collectFieldsValuesForStorage(): TPersistedFields;

    renderIcon(): React.ReactElement | null {
        const iconName = this._getIonIconName();
        if(!iconName) {
            return null;
        }

        return (
            <IonIcon icon={iconName}/>
        )

    }

    protected _getIonIconName(): NullableString {
        return null;
    }

    private _paginator: BookingSearchPaginatorModel;

    private _selectedRecordKey: NullableString = null;
    get selectedRecordKey(): NullableString {
        return this._selectedRecordKey;
    }

    set selectedRecordKey(value: NullableString) {
        runInAction(() => {
            this._selectedRecordKey = value;
        });
    }

    get pages(): BookingSearchResultPageModel[] {
        return this._paginator.pages;
    }

    private _currentPageIndex: number = 0;
    get currentPageIndex(): number {
        return this._currentPageIndex;
    }

    set currentPageIndex(value: number) {
        runInAction(() => {
            this._currentPageIndex = Math.max(0, value);
        });
    }

    getRecordPosition(recordIndex: number): number {
        return this.currentPageIndex * this._paginator.pageSize + recordIndex + 1;
    }

    getAllRecords(): IBookingSearchResultRecordViewModel[] {
        return this.pages.flatMap(p => p.records);
    }

    get hasValidFilters(): boolean {
        return !this.hasErrors();
    }

    async exportCurrentResultsToCSV(): Promise<void> {
        // If you still want the deep‑pagination behaviour keep the next line, otherwise remove it.
        // await this.applyFullSearch();

        const resultModels = this.getAllRecords();
        if (!resultModels.length) {
            return;
        }

        const language =  this.services.language;

        const headers = [
            'PNR',
            language.translate('Flight number'),
            language.translate('Passenger'),
            language.translate('Origin'),
            language.translate('Destination'),
            language.translate('Departure date'),
            language.translate('Departure time'),
            language.translate('Booking status'),
            language.translate('Agent'),
        ];


        const formatStation = (station: NullableStation) => {
            if(!station) {
                return "";
            }

            return `${station.stationName} (${station.stationCode})`;
        }

        const rows = resultModels.map(r => [
            r.pnr,
            r.flightNumber ?? "",
            r.passenger ?? "",
            formatStation(r.origin),
            formatStation(r.destination),
            r.flightDepartureDate ?? "",
            r.flightDepartureTime ?? "",
            r.bookingStatusName ?? "",
            r.agent ?? "",
        ]);

        CsvExporter.download({
            headers,
            records: rows,
            fileName: `booking-search-${new Date().toISOString().slice(0, 10)}.csv`,
        });
    }

    get hasMorePages(): boolean {
        return this._paginator.hasMorePages;
    }

    protected _createFields(): FormFields<TFields> {
        return this._createFilterFields(this._readSavedFilters());
    }
    private _isSearchInProgress: boolean = false;
    get isSearchInProgress(): boolean {
        return this._isSearchInProgress;
    }
    set isSearchInProgress(value: boolean) {
        runInAction(() => {
            this._isSearchInProgress = value;
        });
    }
    async applySearch(): Promise<void> {
        if(this.isSearchInProgress) {
            return;
        }

        this.activateErrorsValidation();
        if(this.hasErrors()) {
            return;
        }

        runInAction(() => {
            this.currentPageIndex = 0;
            this._paginator = new BookingSearchPaginatorModel(this.services);
        });

        await this.nextPage();

        this.resetErrorsValidation();
    }


    async nextPage(): Promise<void> {

        this.isSearchInProgress = true;
        try {
            const userSession = await this.services.user.getSession();
            let searchResult = await this._searchBookings(userSession.token, {
                pageSize: this._paginator.pageSize,
                lastIndex: this._paginator.lastRecordIndex
            });
            runInAction(() => {
                const records = searchResult.map(record => new BookingSearchResultRecordModel(this.services, record));
                this._paginator.addPage(new BookingSearchResultPageModel(records));
                this.currentPageIndex = this._paginator.pages.length - 1;
            });
        } finally {
            this.isSearchInProgress = false;
        }

    }

    clearAllFilters(): void {
        super.clearAllFields();
    }

    clearResults(): void {
        this._paginator.clearResults();
    }

    protected _onFieldChanged(field: IFormField) {
        super._onFieldChanged(field);
        this._saveFiltersToStorage(this._collectFieldsValuesForStorage());
    }

    private _saveFiltersTimerRef: any = null;
    private _saveFiltersToStorage(filters: TPersistedFields): void {
        clearTimeout(this._saveFiltersTimerRef);
        this._saveFiltersTimerRef = setTimeout(() => {
            this.services.userSessionStorage.setJson(UserSessionStorageKeys.BookingsSearchFiltersValues, filters, {
                discriminator: this.type.toString()
            });
        });
    }

    private _readSavedFilters(): TPersistedFields | null {
        return this.services.userSessionStorage.getJson(UserSessionStorageKeys.BookingsSearchFiltersValues, {
            discriminator: this.type.toString()
        });
    }

    protected _formatDateUtcZuluTime(date: NullableDate): string {
        if(date) {
            return this.services.time.formatUtcZuluTime(date);
        }
        return "";
    }

    protected _formatDateYYYY_MM_DD(date: NullableDate): string {
        if(date) {
            return this.services.time.formatYYYY_MM_DD(date);
        }
        return "";
    }
}